// src/utils/inAppBrowser.js
// Detects Instagram / Facebook / Messenger in-app browsers (WebViews) and helps
// push the user out into the real browser (Chrome / Safari).
//
// Why: links shared on IG/FB open inside a cut-down WebView where Google login,
// payments, PWA install, cookies and downloads are flaky. Opening in the system
// browser fixes all of that.
//
// Platform reality:
//   • Android — we CAN force the system browser via an `intent://` URL.
//   • iOS     — Apple gives no scheme to launch *Safari*, BUT Chrome registers its own
//               deep-link scheme (googlechromes://). Navigating to that from inside the
//               IG/FB webview launches Chrome if it's installed. If Chrome isn't there,
//               nothing happens — so we fall back to a "tap ⋯ → Open in Safari" overlay.

const ua = () =>
  (typeof navigator !== 'undefined' && (navigator.userAgent || navigator.vendor)) || '';

// Instagram: "Instagram"  |  Facebook/Messenger: "FBAN", "FBAV", "FB_IAB", "Messenger"
export const isInAppBrowser = () => {
  const s = ua();
  return /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Messenger|FB4A/i.test(s);
};

export const isAndroid = () => /Android/i.test(ua());
export const isIOS = () =>
  /iPhone|iPad|iPod/i.test(ua()) ||
  // iPadOS 13+ reports as Mac; detect via touch points
  (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Build an Android intent:// URL that opens the current page in the system browser.
// We don't pin a package (e.g. com.android.chrome) so it respects the user's default
// browser, and we add a fallback URL in case no browser handles the intent.
const buildAndroidIntentUrl = (url) => {
  const u = new URL(url);
  const hostAndPath = `${u.host}${u.pathname}${u.search}${u.hash}`;
  const scheme = u.protocol.replace(':', '') || 'https';
  const fallback = encodeURIComponent(url);
  return `intent://${hostAndPath}#Intent;scheme=${scheme};S.browser_fallback_url=${fallback};end`;
};

// Chrome-for-iOS deep link: googlechrome:// replaces http://, googlechromes:// replaces https://
const buildChromeIosUrl = (url) => {
  const u = new URL(url);
  const scheme = u.protocol === 'https:' ? 'googlechromes' : 'googlechrome';
  return `${scheme}://${u.host}${u.pathname}${u.search}${u.hash}`;
};

// Attempt to leave the in-app browser. Returns the platform we acted on:
//   'android'      — fired an intent:// redirect (system browser should open)
//   'ios'          — fired a googlechromes:// redirect (Chrome opens if installed)
//   'not-in-app'   — normal browser, nothing to do
export const escapeInAppBrowser = (targetUrl = window.location.href) => {
  if (!isInAppBrowser()) return 'not-in-app';

  if (isAndroid()) {
    try {
      window.location.href = buildAndroidIntentUrl(targetUrl);
    } catch {
      return 'ios';
    }
    return 'android';
  }

  if (isIOS()) {
    try {
      // Opens Chrome if installed; silently no-ops otherwise (overlay handles fallback).
      window.location.href = buildChromeIosUrl(targetUrl);
    } catch {
      /* show overlay */
    }
    return 'ios';
  }

  return 'ios';
};
